import { useMemo, useState } from "react";
import type { SortBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, shuffled, type CheckResult } from "../CheckShell";

interface Props {
  block: SortBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

/** Tap an item, then tap the bin it belongs in. Tap an item inside a bin to send it back. */
export function SortBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const pool = useMemo(() => shuffled(block.items), [block.id]);
  const [placement, setPlacement] = useState<Record<string, string>>({}); // itemId -> binId
  const [active, setActive] = useState<string | null>(null);
  const [wrongIds, setWrongIds] = useState<Set<string>>(new Set());
  const done = check.locked;

  const placeInto = (binId: string) => {
    if (!active || done) return;
    if (check.status === "wrong") check.retry();
    setPlacement(prev => ({ ...prev, [active]: binId }));
    setWrongIds(prev => { const n = new Set(prev); n.delete(active); return n; });
    setActive(null);
  };
  const sendBack = (itemId: string) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setPlacement(prev => { const n = { ...prev }; delete n[itemId]; return n; });
    setWrongIds(prev => { const n = new Set(prev); n.delete(itemId); return n; });
  };
  const allPlaced = block.items.every(it => placement[it.id]);
  const submit = () => {
    const wrong = block.items.filter(it => placement[it.id] !== it.bin).map(it => it.id);
    setWrongIds(new Set(wrong));
    check.submit(wrong.length === 0);
  };
  const effectivePlacement = done && check.status === "revealed" ? Object.fromEntries(block.items.map(it => [it.id, it.bin])) : placement;
  const unplaced = pool.filter(it => !effectivePlacement[it.id]);

  const chip = (it: SortBlock["items"][number], inBin: boolean, binId?: string) => {
    const isWrong = wrongIds.has(it.id) && !done;
    const isActive = active === it.id;
    return (
      <button
        key={it.id}
        type="button"
        disabled={done}
        onClick={(e) => {
          e.stopPropagation();
          if (!inBin) { setActive(isActive ? null : it.id); return; }
          // Inside a bin: if another item is selected, a tap anywhere in the box (even on a chip)
          // means "put it here"; otherwise tapping a placed chip sends it back to the pool.
          if (active && active !== it.id && binId) placeInto(binId); else sendBack(it.id);
        }}
        className={`rounded-xl border-2 px-3 py-2 text-sm md:text-base font-medium transition-colors ${
          done ? (check.status === "correct" || effectivePlacement[it.id] === it.bin ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30" : "border-rose-400 bg-rose-50 dark:bg-rose-900/30")
          : isWrong ? "border-rose-400 bg-rose-50 dark:bg-rose-900/30"
          : isActive ? "border-primary bg-primary text-primary-foreground shadow-md scale-105"
          : "border-border bg-card hover:border-primary/50"
        }`}
        data-testid={`sort-item-${it.id}`}
      >
        <RichText text={it.text} inline />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <p className="text-sm text-muted-foreground">{active ? "Now tap the box it belongs in." : "Tap an item, then tap a box."}</p>

      <div className="min-h-[56px] rounded-2xl border-2 border-dashed border-border p-3 flex flex-wrap gap-2">
        {unplaced.length === 0 && <span className="text-sm text-muted-foreground">All sorted! Press Check.</span>}
        {unplaced.map(it => chip(it, false))}
      </div>

      <div className={`grid gap-3 ${block.bins.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {block.bins.map(bin => {
          const items = block.items.filter(it => effectivePlacement[it.id] === bin.id);
          return (
            <div
              key={bin.id}
              role="button"
              tabIndex={active && !done ? 0 : -1}
              aria-disabled={done || !active}
              onClick={() => placeInto(bin.id)}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && active && !done) { e.preventDefault(); placeInto(bin.id); } }}
              className={`text-left rounded-2xl border-2 p-3 min-h-[120px] transition-all ${active && !done ? "border-primary/70 bg-primary/5 hover:bg-primary/10 cursor-pointer animate-pulse" : "border-border bg-muted/30 cursor-default"}`}
              data-testid={`sort-bin-${bin.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {bin.emoji && <span className="text-xl">{bin.emoji}</span>}
                <RichText text={bin.label} inline className="font-bold" />
                <span className="ml-auto text-xs text-muted-foreground">{items.length}</span>
              </div>
              <div className="flex flex-wrap gap-2 min-h-[40px]">
                {items.map(it => chip(it, true, bin.id))}
              </div>
            </div>
          );
        })}
      </div>

      <CheckFooter
        check={check}
        canSubmit={allPlaced}
        onSubmit={submit}
        feedbackWrong={wrongIds.size ? `${wrongIds.size} ${wrongIds.size === 1 ? "item is" : "items are"} in the wrong box. Move the red ones.` : undefined}
        revealAnswer={<p>The items have been moved to the right boxes.</p>}
      />
    </div>
  );
}
